/**
 * ZodiacFEN - Forsyth-Edwards Notation adapted for Zodiac game
 * Handles serialization and deserialization of game states
 */
import { GameState, PieceType } from "../types";

export class ZodiacFEN {
  /**
   * Converts the current game state to a FEN-style string
   * @param gameState The game state to encode
   * @returns FEN string representation of the board
   */
  static encode(gameState: GameState): string {
    let fenString = "";

    // Add turn indicator at the beginning if not first turn
    if (gameState.turnCount > 0) {
      if (!gameState.isLightTurn) {
        fenString = "d" + fenString;
      }
    }

    // Process the board state column by column (instead of row by row)
    // Starting from the bottom left (0,0) and moving up first
    const numCols = gameState.board[0].length; // Should be 5
    const numRows = gameState.board.length; // Should be 8

    // For each column
    for (let col = 0; col < numCols; col++) {
      if (col !== 0) {
        // Add separator between columns
        fenString += "/";
      }

      let emptyCount = 0;

      // For each row in this column, moving from bottom (Light side, row 0) to top (Dark side, row numRows-1)
      for (let row = 0; row < numRows; row++) {
        const square = gameState.board[row][col];
        const pieces = square.Pieces;

        // If square is empty, increment counter
        if (pieces.length === 0) {
          emptyCount++;
          continue;
        }

        // If we had empty squares before this piece, add the count
        if (emptyCount > 0) {
          fenString += emptyCount;
          emptyCount = 0;
        }

        // Encode all pieces on this square with "+" between them
        // RULE: When a virus is stacked with another piece, the virus is ALWAYS second
        // Example: "p+v" or "P+V" (infected penguin), never "v+p" or "V+p"
        const pieceStrings = [];
        const virusPieces = [];

        for (const piece of pieces) {
          if (piece.type === PieceType.Virus) {
            virusPieces.push(this.encodePiece(piece));
          } else {
            pieceStrings.push(this.encodePiece(piece));
          }
        }

        // Add virus pieces at the end for consistency
        pieceStrings.push(...virusPieces);

        // Join pieces with "+" to indicate they share the same square
        fenString += pieceStrings.join("+");
      }

      // If column ends with empty squares, add the count
      if (emptyCount > 0) {
        fenString += emptyCount;
      }
    }

    // REMOVED: |bonus: suffix causes FEN parsing issues in other systems
    // The "-" prefix at the start of the FEN is sufficient to indicate bonus turns
    // if (gameState.activeBonusTurnPieceId) {
    //   fenString += `|bonus:${gameState.activeBonusTurnPieceId}`;
    // }

    return fenString;
  }

  /**
   * Encodes a single piece with all its attributes
   * @param piece The piece to encode
   * @returns String representation of the piece
   */
  private static encodePiece(piece: any): string {
    let pieceString = "";

    // Get base piece character
    const pieceChar = this.getPieceChar(piece.type);

    // Set case based on piece ownership
    // Uppercase for light, lowercase for dark
    pieceString += piece.isLight
      ? pieceChar.toUpperCase()
      : pieceChar.toLowerCase();

    // Add orientation for directional pieces like Penguin
    if (piece.type === PieceType.Penguin) {
      pieceString += this.getDirectionSymbol(piece.direction);
    }

    // Add special ability markers
    pieceString += this.encodeSpecialAbilities(piece);

    return pieceString;
  }

  /**
   * Maps a direction string to the FEN symbol for Penguin orientation
   * Uses Unicode arrows by default, falls back to ASCII if useAsciiDirections is true
   */
  private static getDirectionSymbol(
    direction: string | null | undefined
  ): string {
    switch (direction) {
      case "up":
        return "\u2191"; // ↑
      case "up-right":
        return "\u2197"; // ↗
      case "right":
        return "\u2192"; // →
      case "down-right":
        return "\u2198"; // ↘
      case "down":
        return "\u2193"; // ↓
      case "down-left":
        return "\u2199"; // ↙
      case "left":
        return "\u2190"; // ←
      case "up-left":
        return "\u2196"; // ↖
      default:
        return "\u2191"; // Default to Up (↑)
    }
  }

  /**
   * Gets the character representation for a piece type
   * @param pieceType The type of the piece
   * @returns Character representation
   */
  public static getPieceChar(pieceType: string): string {
    switch (pieceType) {
      case "Bear":
        return "B";
      case "Crane":
        return "C";
      case "Frog":
        return "F";
      case "Goat":
        return "G";
      case "Monkey":
        return "M";
      case "Fungi":
        return "U";
      case "Octopus":
        return "O";
      case "Penguin":
        return "P";
      case "Rhino":
        return "R";
      case "Snake":
        return "S";
      case "Tree":
        return "T";
      case "Wolf":
        return "W";
      case "Virus":
        return "V";
      default:
        return "x"; // Unknown piece
    }
  }

  /**
   * Encodes special abilities and states of the piece
   * @param piece The piece to encode
   * @returns String representation of special abilities
   */
  private static encodeSpecialAbilities(piece: any): string {
    let special = "";

    // Entanglement
    if (piece.isEntangled) {
      special += "\u26D3"; // ⛓
    }

    // Poisoned (timing preserved in Firebase statusEffects, not FEN)
    if (piece.poisonedStatus && piece.poisonedStatus.isPoisoned) {
      special += "☣";
    }

    // Meadowed
    if (piece.isMeadowed) {
      special += "⚘";
    }

    // Zombified (timing preserved in Firebase statusEffects, not FEN)
    if (piece.isZombified || piece.zombifiedStatus?.isZombified) {
      special += "z";
    }

    // Special single-use abilities
    if (
      (piece.type === PieceType.Frog && !piece.hasLilyJumped) ||
      (piece.type === PieceType.Rhino && !piece.hasCharged) ||
      (piece.type === PieceType.Snake && !piece.hasShed)
    ) {
      special += "✳️";
    }

    // Special state for specific pieces
    if (
      (piece.type === PieceType.Wolf && piece.isLone) ||
      (piece.type === PieceType.Goat && piece.isFainted)
    ) {
      special += "$";
    }

    // King status
    if (piece.isKing) {
      special += piece.isLight ? "♛" : "♕";
    }

    // Rhino first-move marker (for lane ability)
    // Use "○" (White Circle) to indicate a Rhino that hasn't moved yet
    // This has precedent in chess notation (castling rights, virgin pieces)
    if (piece.type === PieceType.Rhino && !piece.hasMoved) {
      special += "○";
    }

    return special;
  }

  /**
   * Checks if the given FEN string represents a valid game state
   * @param fenString The FEN string to validate
   * @returns True if valid, false otherwise
   */
  static validate(fenString: string): boolean {
    // Perform basic validation of the FEN string structure
    if (!fenString) return false;

    // Remove turn indicator if present
    if (fenString.startsWith("d")) {
      fenString = fenString.substring(1);
    }

    // Split into columns
    const columns = fenString.split("/");
    const numRows = 8; // Board height
    const numCols = columns.length; // Should be 5

    // Check if we have the expected number of columns
    if (numCols === 0) return false;

    // Validate each column
    for (let colIndex = 0; colIndex < columns.length; colIndex++) {
      let rowCount = 0;

      for (let i = 0; i < columns[colIndex].length; i++) {
        const char = columns[colIndex][i];

        // If it's a number, it represents empty squares
        if (!isNaN(parseInt(char))) {
          rowCount += parseInt(char);
        } else if (char === "+") {
          // '+' doesn't increase row count, it just connects pieces on the same square
          continue;
        } else {
          // Check if this is a piece character
          if (this.isPieceChar(char)) {
            // Only count actual piece characters
            if (i === 0 || columns[colIndex][i - 1] !== "+") {
              rowCount++;
            }
          }
          // If it's a special character, we just skip it (don't count it)
          // The loop will naturally move to the next character
        }
      }

      // Each column should have exactly 8 rows
      if (rowCount !== 8) return false;
    }

    return true;
  }

  /**
   * Checks if the character represents a piece type
   * @param char The character to check
   * @returns True if it's a piece character, false otherwise
   */
  private static isPieceChar(char: string): boolean {
    const upperChar = char.toUpperCase();
    return [
      "B",
      "C",
      "F",
      "G",
      "M",
      "U",
      "O",
      "P",
      "R",
      "S",
      "T",
      "W",
      "V",
    ].includes(upperChar);
  }

  /**
   * Checks if the character is a special character rather than a new piece
   * @param char The character to check
   * @returns True if it's a special character, false otherwise
   */
  private static isSpecialChar(char: string): boolean {
    const specialChars = [
      // Direction indicators (Unicode arrows and ASCII fallback)
      "\u2191", // ↑ Up
      "\u2193", // ↓ Down
      "\u2190", // ← Left
      "\u2192", // → Right
      "\u2196", // ↖ Up-left
      "\u2197", // ↗ Up-right
      "\u2199", // ↙ Down-left
      "\u2198", // ↘ Down-right
      "^", // Up (ASCII fallback)
      "_", // Down (ASCII fallback)
      "<", // Left (ASCII fallback)
      ">", // Right (ASCII fallback)
      "{", // Up-left (ASCII fallback)
      "}", // Up-right (ASCII fallback)
      "(", // Down-left (ASCII fallback)
      ")", // Down-right (ASCII fallback)

      // Status effects
      "\u26D3", // ⛓ Entangled
      "\u2620", // ☠ (poisoned, no VS16)
      "\u2620\uFE0F", // ☠️ (poisoned, with VS16)
      "⚘", // Meadowed
      "#", // Meadowed (ASCII version)
      "☠", // Zombified
      "z", // Zombified (ASCII version)
      "i", // Infected
      "r", // Riding
      "h", // Housing
      "l", // Lone
      "d", // Dormant
      "✳️", // Special single-use ability (Lily pad Frog / Mega Rhino / Snake Shed) - emoji version
      "\u2733", // ✳ Eight-spoked asterisk (base Unicode without variation selector)
      "\uFE0F", // Variation Selector-16 (makes previous char display as emoji)
      "*", // Special single-use ability (ASCII version)
      "$", // Special state - (Lone wolf / Shadowed Octopus / Fainted Goat / High Monkey)
      "K", // Light king
      "k", // Dark king
      "♛", // Light king
      "♕", // Dark king
      "○", // Rhino first-move marker (unmoved, lane ability available)
    ];
    return specialChars.includes(char) || char === "+";
  }

  /**
   * Converts the current game state to a FEN-style string
   * with the opponent's king information hidden based on perspective
   * @param gameState The game state to encode
   * @param currentPlayerIsLight Whether the current player is light
   * @returns FEN string with hidden king information
   */
  static encodeWithHiddenKings(
    gameState: GameState,
    currentPlayerIsLight: boolean
  ): string {
    let fenString = "";

    // Add turn indicator at the beginning if not first turn
    if (gameState.turnCount > 0) {
      if (!gameState.isLightTurn) {
        fenString = "d" + fenString;
      }
    }

    // Process the board state column by column (instead of row by row)
    // Starting from the bottom left (0,0) and moving up first
    const numCols = gameState.board[0].length; // Should be 5
    const numRows = gameState.board.length; // Should be 8

    // For each column
    for (let col = 0; col < numCols; col++) {
      if (col !== 0) {
        // Add separator between columns
        fenString += "/";
      }

      let emptyCount = 0;

      // For each row in this column, moving from bottom (Light side, row 0) to top (Dark side, row numRows-1)
      for (let row = 0; row < numRows; row++) {
        const square = gameState.board[row][col];
        const pieces = square.Pieces;

        // If square is empty, increment counter
        if (pieces.length === 0) {
          emptyCount++;
          continue;
        }

        // If we had empty squares before this piece, add the count
        if (emptyCount > 0) {
          fenString += emptyCount;
          emptyCount = 0;
        }

        // Encode all pieces on this square with "+" between them
        const pieceStrings = [];
        for (const piece of pieces) {
          // Hide king information for opponent pieces
          const isOpponentPiece = piece.isLight !== currentPlayerIsLight;

          if (isOpponentPiece) {
            // For opponent pieces, don't encode king status
            pieceStrings.push(this.encodePieceWithoutKing(piece));
          } else {
            // For current player's pieces, include king status
            pieceStrings.push(this.encodePiece(piece));
          }
        }

        // Join pieces with "+" to indicate they share the same square
        fenString += pieceStrings.join("+");
      }

      // If column ends with empty squares, add the count
      if (emptyCount > 0) {
        fenString += emptyCount;
      }
    }

    // REMOVED: |bonus: suffix causes FEN parsing issues in other systems
    // The "-" prefix at the start of the FEN is sufficient to indicate bonus turns
    // if (gameState.activeBonusTurnPieceId) {
    //   fenString += `|bonus:${gameState.activeBonusTurnPieceId}`;
    // }

    return fenString;
  }

  /**
   * Encodes a piece without including king status
   * @param piece The piece to encode
   * @returns String representation of the piece without king status
   */
  private static encodePieceWithoutKing(piece: any): string {
    let pieceString = "";

    // Get base piece character
    const pieceChar = this.getPieceChar(piece.type);

    // Set case based on piece ownership
    // Uppercase for light, lowercase for dark
    pieceString += piece.isLight
      ? pieceChar.toUpperCase()
      : pieceChar.toLowerCase();

    // Add orientation for directional pieces like Penguin
    if (piece.type === PieceType.Penguin) {
      pieceString += this.getDirectionSymbol(piece.direction);
    }

    // Add special ability markers EXCEPT king status
    pieceString += this.encodeSpecialAbilitiesWithoutKing(piece);

    return pieceString;
  }

  /**
   * Encodes special abilities excluding king status
   * @param piece The piece to encode
   * @returns String representation of special abilities without king status
   */
  private static encodeSpecialAbilitiesWithoutKing(piece: any): string {
    let special = "";

    // Entanglement
    if (piece.isEntangled) {
      special += "\u26D3"; // ⛓
    }

    // Poisoned
    if (piece.poisonedStatus && piece.poisonedStatus.isPoisoned) {
      special += "☣";
    }

    // Meadowed
    if (piece.isMeadowed) {
      special += "⚘";
    }

    // Zombified
    if (piece.isZombified || piece.zombifiedStatus?.isZombified) {
      special += "z";
    }

    // Special single-use abilities
    if (
      (piece.type === PieceType.Frog && !piece.hasLilyJumped) ||
      (piece.type === PieceType.Rhino && !piece.hasCharged) ||
      (piece.type === PieceType.Snake && !piece.hasShed)
    ) {
      special += "✳️";
    }

    // Special state for specific pieces
    if (
      (piece.type === PieceType.Wolf && piece.isLone) ||
      (piece.type === PieceType.Goat && piece.isFainted)
    ) {
      special += "$";
    }

    // Rhino first-move marker (for lane ability)
    // Use "○" (White Circle) to indicate a Rhino that hasn't moved yet
    if (piece.type === PieceType.Rhino && !piece.hasMoved) {
      special += "○";
    }

    return special;
  }

  /**
   * Reveals hidden king information in a gameplay FEN
   * @param fenString The gameplay FEN with hidden king information
   * @param lightKingType The piece type of the light king
   * @param darkKingType The piece type of the dark king
   * @returns FEN string with all king information revealed
   */
  static revealKingsInFEN(
    fenString: string,
    lightKingType: string,
    darkKingType: string
  ): string {
    // Parse the FEN and add king markers to the appropriate pieces
    let result = fenString;

    // Get the character representations for the kings
    const lightKingChar = this.getPieceChar(lightKingType).toUpperCase();
    const darkKingChar = this.getPieceChar(darkKingType).toLowerCase();

    // Add king markers to all matching pieces
    // For light king (uppercase)
    result = result.replace(
      new RegExp(lightKingChar + "(?![kK])", "g"),
      lightKingChar + "K"
    );

    // For dark king (lowercase)
    result = result.replace(
      new RegExp(darkKingChar + "(?![kK])", "g"),
      darkKingChar + "k"
    );

    return result;
  }

  /**
   * Converts the current game state to a FEN-style string
   * with all king information hidden (for both sides)
   * @param gameState The game state to encode
   * @returns FEN string with all king information hidden
   */
  static encodeWithoutKings(gameState: GameState): string {
    let fenString = "";

    // NOTE: Turn indicator is NOT included for threefold repetition detection
    // The same board position should count as repetition regardless of whose turn it is
    // (Standard chess threefold repetition also ignores whose turn it is)

    const numCols = gameState.board[0].length; // Should be 5
    const numRows = gameState.board.length; // Should be 8

    for (let col = 0; col < numCols; col++) {
      if (col !== 0) {
        fenString += "/";
      }
      let emptyCount = 0;
      for (let row = 0; row < numRows; row++) {
        const square = gameState.board[row][col];
        const pieces = square.Pieces;
        if (pieces.length === 0) {
          emptyCount++;
          continue;
        }
        if (emptyCount > 0) {
          fenString += emptyCount;
          emptyCount = 0;
        }
        const pieceStrings = [];
        for (const piece of pieces) {
          pieceStrings.push(this.encodePieceWithoutKing(piece));
        }
        fenString += pieceStrings.join("+");
      }
      if (emptyCount > 0) {
        fenString += emptyCount;
      }
    }

    // REMOVED: |bonus: suffix causes FEN parsing issues in other systems
    // The "-" prefix at the start of the FEN is sufficient to indicate bonus turns
    // if (gameState.activeBonusTurnPieceId) {
    //   fenString += `|bonus:${gameState.activeBonusTurnPieceId}`;
    // }

    return fenString;
  }
}
